import { Provider } from '@angular/core';
import { Route } from '@angular/router';
import { ENTITY_DIALOG_OUTLET, EntityDialogDeactivateSource, EntityDialogs, EntityDialogSource } from './entity.dialog.contracts';
import { entityOperationRouteGuard } from '@workbuddy/ui/v2/blocks/entity.record/entity.operation.route.guard';
import { entityDialogRouteGuard } from './entity.dialog.route.guard';
import { EntityDialogService } from './entity.dialog.service';

/**
 * Provided on the shell ROUTE rather than only on ShellComponent: `canDeactivate` resolves against the
 * route injector, where component-level providers are invisible (NG0201). The route lives as long as the
 * shell, so every board injects the same instance and none creates a nested host.
 *
 * The **service** implements all three tokens because the route guard must inject the deactivate source
 * before the host component exists.
 */
export function provideEntityDialogs(): Provider[] {

  return [
    EntityDialogService,
    { provide: EntityDialogs, useExisting: EntityDialogService },
    { provide: EntityDialogDeactivateSource, useExisting: EntityDialogService },
    { provide: EntityDialogSource, useExisting: EntityDialogService },
  ];
}

/**
 * One config for every entity and operation, on the `dialog` outlet.
 *
 * `:slug` rather than the entity name because `ActivityEntityName` values are not URL-safe — see
 * `EntityManifest.slug`. Routes are matched per-outlet, so `dialog` is already the namespace and no path
 * prefix is needed.
 *
 * The id is a matrix param because create has none.
 */
export const entityDialogRoute: Route = {
  path: ':slug/:operation',
  outlet: ENTITY_DIALOG_OUTLET,
  loadComponent: () => import('./entity.dialog.host.component').then(m => m.EntityDialogHostComponent),
  // Same gate as the full-page route. A denial here leaves the outlet unactivated rather than
  // redirecting: an empty dialog announcing what you cannot do is worse than the action being absent.
  canActivate: [entityOperationRouteGuard],
  canDeactivate: [entityDialogRouteGuard],
  // The side nav builds itself from the shell route's children and reads `data.title` /
  // `data.restrictions` unguarded, so a child with no `data` throws and takes the whole nav with it.
  data: { hidden: true, restrictions: [] },
};
